import type { PerimeterXEnforcerChallengeWithBlockedUrl } from '@change-corgi/core/perimeterx';

export type Options = {
	pxResponse: PerimeterXEnforcerChallengeWithBlockedUrl;
};

export type Result = {
	success: boolean;
};

export type CaptchaHostState =
	| { status: 'idle' }
	| { status: 'pending'; pxResponse: PerimeterXEnforcerChallengeWithBlockedUrl; challengeId: number };

const IDLE_STATE: CaptchaHostState = { status: 'idle' };

export class CaptchaHostStore {
	private state: CaptchaHostState = IDLE_STATE;
	private resolver: ((success: boolean) => void) | null = null;
	private challengeCount = 0;
	private listeners = new Set<() => void>();

	private notify(): void {
		this.listeners.forEach((listener) => {
			listener();
		});
	}

	// any close without a vendor result settles `false`, so no awaiting caller can ever hang
	private settle(success: boolean): void {
		const resolve = this.resolver;
		this.resolver = null;
		if (this.state.status !== 'idle') {
			this.state = IDLE_STATE;
			this.notify();
		}
		resolve?.(success);
	}

	subscribe(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	getSnapshot(): CaptchaHostState {
		return this.state;
	}

	// challenges only fire from client async handlers, so the host renders null on the server
	// oxlint-disable-next-line class-methods-use-this
	getServerSnapshot(): CaptchaHostState {
		return IDLE_STATE;
	}

	// a superseding open() resolves the prior promise `false`, then opens fresh — the vendor
	// requires exactly one `#px-captcha` on the page
	async open({ pxResponse }: Options): Promise<boolean> {
		this.resolver?.(false);
		this.resolver = null;
		this.challengeCount += 1;
		this.state = { status: 'pending', pxResponse, challengeId: this.challengeCount };
		this.notify();
		// oxlint-disable-next-line promise/avoid-new
		return new Promise<boolean>((resolve) => {
			this.resolver = resolve;
		});
	}

	resolve(result: Result): void {
		this.settle(result.success);
	}

	// ESC, supersede, or host unmount — anything that closes without a vendor result
	dismiss(): void {
		this.settle(false);
	}
}
